import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { BooleanInput } from '@angular/cdk/coercion';
import { Subject, takeUntil } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { MatMenuTrigger } from '@angular/material/menu';
import { UserService } from 'app/core/user/user.service';
import { AuthService} from "app/core/auth/auth.service";
import {UserModel} from "../../../core/user/user.model";
import {state} from "@angular/animations";
import {fuseAnimations} from "@fuse/animations";
import {environment} from "../../../../environments/environment";
import { AccountSettingsModalComponent } from './modal/settings/account-settings-modal.component';
import { BillingModalComponent } from './modal/billing/billing-modal.component';
import { HelpChatComponent } from './modal/help-chat/help-chat.component';
import { UpdatePlanModalComponent } from './modal/billing/components/plan-subscription/modals/update-plan-modal.component';
import { AlertService } from 'app/components/ui/alert/alert.service';
import { MessagesConstants } from 'app/constants/messages.constants';
import { TabPermissionService } from 'app/core/auth/tab-permission.service';

@Component({
    selector       : 'user',
    templateUrl    : './user.component.html',
    encapsulation  : ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs       : 'user',
    animations: fuseAnimations
})
export class UserComponent implements OnInit, OnDestroy
{
    /* eslint-disable @typescript-eslint/naming-convention */
    static ngAcceptInputType_showAvatar: BooleanInput;
    /* eslint-enable @typescript-eslint/naming-convention */

    @Input() showAvatar: boolean = true;
    user: any;
    churchData: any = null;
    showDropdown: boolean = false;
    canAccessBilling: boolean = true; // default true para evitar flash antes de carregar permissões
    canAccessHealth: boolean = false; // "Saúde do Sistema" visível APENAS com aud-access (padrão da seção Administração do modal de Configurações)

    @ViewChild('matMenuTrigger') private _matMenuTrigger: MatMenuTrigger;

    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Constructor
     */
    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _router: Router,
        private _authService: AuthService,
        private _userService: UserService,
        private _matDialog: MatDialog,
        private _alertService: AlertService,
        private _tabPermission: TabPermissionService
    )
    {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void
    {
        if(!this.user)
        {
            let user = JSON.parse(this._authService.getLSUser);
            this.user = user;
        }
        else
        {
            // Subscribe to user changes
            this._userService.user$
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe((user: any) => {
                    this.user = user;

                    // Mark for check
                    this._changeDetectorRef.markForCheck();
                });
        }

        // Load church data from localStorage
        this.loadChurchData();

        // Check billing permission synchronously (from cache) and via API
        if (this._tabPermission.loaded) {
            this.canAccessBilling = this._tabPermission.canAccess('bil-access');
            this.canAccessHealth = this._tabPermission.canAccess('aud-access');
        }

        this._tabPermission.loadPermissions(true)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
                next: () => {
                    this.canAccessBilling = this._tabPermission.canAccess('bil-access');
                    this.canAccessHealth = this._tabPermission.canAccess('aud-access');
                    this._changeDetectorRef.markForCheck();
                },
                error: () => {
                    // Em caso de erro (não-401), mantém visível como fallback
                    this.canAccessBilling = true;
                    this._changeDetectorRef.markForCheck();
                }
            });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void
    {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Sign out
     */
    signOut(): void
    {
        // Sign out
        this._authService.signOut();
        this._router.navigate(['/login']);
    }

    /**
     * Abre a tela "Saúde do Sistema" (/health) — item do menu de usuário
     * IMEDIATAMENTE ABAIXO de "Configurações", gatedo por `aud-access`
     * (gate real é server-side; o guard da rota é apenas AuthGuard).
     */
    openHealthSystem(): void
    {
        // Fecha o dropdown do usuário antes de navegar (item custom não fecha automaticamente)
        this._matMenuTrigger?.closeMenu();
        this._router.navigate(['/health']);
    }

    /**
     * Open account settings modal
     */
    openAccountSettings(): void
    {
        // Fecha o dropdown do usuário antes de abrir o modal (evita backdrop residual do menu)
        this._matMenuTrigger?.closeMenu();

        const accountSettingsDialogRef = this._matDialog.open(AccountSettingsModalComponent, {
            autoFocus: false,
            data: {},
            disableClose: false,
            width: '95%',
            maxWidth: '700px',
            height: '95%',
            maxHeight: '600px',
        });

        accountSettingsDialogRef.afterClosed().subscribe(result => {
            if(result && result.action === 'reload')
            {
                // Ação após fechar o modal (ex: recarregar dados do usuário)
                console.log('Modal fechado com ação de reload');
            }
        });
    }

    /**
     * Open billing modal
     */
    openBillingModal(): void
    {
        // Fecha o dropdown do usuário antes de abrir o modal (evita backdrop residual do menu)
        this._matMenuTrigger?.closeMenu();

        const billingDialogRef = this._matDialog.open(BillingModalComponent, {
            autoFocus: false,
            data: {},
            disableClose: false,
            width: '95%',
            maxWidth: '750px',
            height: '95%',
            maxHeight: '600px',
        });

        billingDialogRef.afterClosed().subscribe(result => {
            if(result && result.action === 'reload')
            {
                // Ação após fechar o modal
                console.log('Modal de faturamento fechado com ação de reload');
            }
        });
    }

    /**
     * Open help chat modal (Ajuda do sistema)
     */
    openHelpChat(): void
    {
        // Fecha o dropdown do usuário antes de abrir o modal (evita backdrop residual do menu)
        this._matMenuTrigger?.closeMenu();

        const helpChatDialogRef = this._matDialog.open(HelpChatComponent, {
            autoFocus: false,
            data: {},
            disableClose: false,
            width: '95%',
            maxWidth: '720px',
            height: '95%',
            maxHeight: '640px',
        });

        helpChatDialogRef.afterClosed().subscribe(result => {
            if (result && result.action === 'reload')
            {
                // Ação após fechar o modal (ex: recarregar dados do usuário)
                console.log('Modal Ajuda fechado com ação de reload');
            }
        });
    }

    /**
     * Open update plan modal
     */
    openUpdatePlanModal(): void
    {
        // Fecha o dropdown do usuário antes de abrir o modal (evita backdrop residual do menu)
        this._matMenuTrigger?.closeMenu();

        // Abre o modal diretamente, a busca de dados será feita dentro do modal
        const dialogRef = this._matDialog.open(UpdatePlanModalComponent, {
            width: '650px',
            maxWidth: '95vw',
            maxHeight: '90vh',
            disableClose: false,
            autoFocus: false,
            panelClass: 'update-plan-modal-panel',
            data: {}
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result?.action === 'updated') {
                this._alertService.success('Plano atualizado com sucesso! Recarregue a página para ver as alterações.', { autoClose: true });
            }
        });
    }

    /**
     * Load church data from localStorage
     */
    loadChurchData(): void {
        try {
            const churchDataString = this._authService.getLSChurch;
            if (churchDataString) {
                this.churchData = JSON.parse(churchDataString);
                this._changeDetectorRef.markForCheck();
            }
        } catch (error) {
            console.error('Error loading church data:', error);
            this.churchData = null;
        }
    }

    /**
     * Get plan display name
     */
    getPlanDisplayName(planName: string): string {
        const planNames: { [key: string]: string } = {
            [MessagesConstants.PLAN_KEY_BRONZE]: MessagesConstants.PLAN_NAME_BRONZE,
            [MessagesConstants.PLAN_KEY_SILVER]: MessagesConstants.PLAN_NAME_SILVER,
            [MessagesConstants.PLAN_KEY_GOLD]: MessagesConstants.PLAN_NAME_GOLD,
            [MessagesConstants.PLAN_KEY_DIAMOND]: MessagesConstants.PLAN_NAME_DIAMOND,
            [MessagesConstants.PLAN_KEY_NO_SUBSCRIPTION]: MessagesConstants.PLAN_NAME_NO_SUBSCRIPTION
        };
        return planNames[planName?.toLowerCase()] || planName;
    }

    /**
     * Get plan description
     */
    getPlanDescription(planName: string): string {
        const descriptions: { [key: string]: string } = {
            [MessagesConstants.PLAN_KEY_BRONZE]: MessagesConstants.PLAN_DESCRIPTION_BRONZE,
            [MessagesConstants.PLAN_KEY_SILVER]: MessagesConstants.PLAN_DESCRIPTION_SILVER,
            [MessagesConstants.PLAN_KEY_GOLD]: MessagesConstants.PLAN_DESCRIPTION_GOLD,
            [MessagesConstants.PLAN_KEY_DIAMOND]: MessagesConstants.PLAN_DESCRIPTION_DIAMOND,
            [MessagesConstants.PLAN_KEY_NO_SUBSCRIPTION]: MessagesConstants.PLAN_DESCRIPTION_NO_SUBSCRIPTION
        };
        return descriptions[planName?.toLowerCase()] || MessagesConstants.BILLING_FREE_PLAN_DESCRIPTION;
    }

    protected readonly state = state;
    protected readonly environment = environment;
    protected readonly MessagesConstants = MessagesConstants;
}
